import React, { ReactElement, useMemo } from 'react';
import {
    TextField,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
    Dropdown,
    DatePicker,
    DayOfWeek,
    IconButton,
    Text,
    TooltipHost,
} from '@fluentui/react';
import { dayPickerStrings } from '../../../../components/PeriodPicker';
import { Period, PeriodState } from '../../../../api/generated/data-contracts';
import { useConfigValuesState } from '../../../../hooks/useConfigValue';
import { format } from '../../../../helpers/date';

interface IProps {
    allPeriods: Period[];
    savePeriod: (period: Period) => void;
    changeStartDate: (period: Period, date: Date) => void;
    changeEndDate: (period: Period, date: Date) => void;
    changeName: (period: Period, name: string) => void;
    changePeriodState(period: Period, optionKey: PeriodState);
    deletePeriod: (period: Period) => void;
}

const defaultDatePickerProps = {
    firstDayOfWeek: DayOfWeek.Monday,
    strings: dayPickerStrings,
    placeholder: 'Select a date...',
    ariaLabel: 'Select a date',
};

const _columns: IColumn[] = [
    { key: 'name', name: 'Name', fieldName: 'name', isRowHeader: true, minWidth: 260, maxWidth: 260, isResizable: true },
    { key: 'startDate', name: 'Start Date', fieldName: 'startDate', minWidth: 160, maxWidth: 160, isResizable: true },
    { key: 'endDate', name: 'End Date', fieldName: 'endDate', minWidth: 160, maxWidth: 160, isResizable: true },
    { key: 'state', name: 'State', fieldName: 'state', minWidth: 140, maxWidth: 140, isResizable: true },
    { key: 'saveRemove', name: '', fieldName: 'saveRemove', minWidth: 80, maxWidth: 80, isResizable: true },
    // { key: 'remove', name: '', fieldName: 'remove', minWidth: 40, maxWidth: 40, isResizable: true },
];

export const PeriodListPicker: React.FC<IProps> = props => {
    const allAdminPeriods = useMemo<Period[]>(() => props.allPeriods, [props.allPeriods]);
    const periodListItems = useMemo<Period[]>(
        () => props.allPeriods.toSorted((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()),
        [props.allPeriods],
    );

    const {
        initialData: {
            configuration: { dateFormat },
        },
    } = useConfigValuesState();

    const onRenderColumn = (item: Period, index: number, column: IColumn): ReactElement => {
        switch (column.key) {
            case 'name':
                return (
                    <TextField
                        key={item.name}
                        defaultValue={item.name}
                        onBlur={e => props.changeName(item, e.target.value)}
                        styles={{ root: { marginRight: 32 } }}
                        disabled={!item.id}
                    />
                );
            case 'state':
                return (
                    <Dropdown
                        defaultSelectedKey={item.state}
                        options={
                            [
                                { key: 'Open', text: 'Open' },
                                { key: 'Pending', text: 'Pending' },
                                { key: 'Closed', text: 'Closed' },
                            ] as { key: PeriodState; text: string }[]
                        }
                        onChanged={option => props.changePeriodState(item, option.key as PeriodState)}
                        styles={{ dropdown: { width: 100, marginRight: 10 } }}
                    />
                );
            case 'startDate':
                return item.id ? (
                    <Text styles={{ root: { display: 'flex', height: '100%', alignItems: 'center' } }}>{format(new Date(item.startDate), dateFormat)}</Text>
                ) : (
                    <DatePicker onSelectDate={date => props.changeStartDate(item, date)} {...defaultDatePickerProps} value={new Date(item.startDate)} />
                );
            case 'endDate': {
                return item.id ? (
                    <Text styles={{ root: { display: 'flex', height: '100%', alignItems: 'center' } }}>{format(new Date(item.endDate), dateFormat)}</Text>
                ) : (
                    <DatePicker onSelectDate={date => props.changeEndDate(item, date)} {...defaultDatePickerProps} value={new Date(item.endDate)} />
                );
            }

            case 'saveRemove': {
                if (!item.id) {
                    return (
                        <>
                            <IconButton
                                iconProps={{ iconName: 'Save' }}
                                onClick={() => {
                                    props.savePeriod(item);
                                }}
                            />
                            <IconButton
                                iconProps={{ iconName: 'Delete' }}
                                onClick={() => {
                                    props.deletePeriod(item);
                                }}
                            />
                        </>
                    );
                }
                if (allAdminPeriods.find(_ => _.id === item.id)?.canBeDeleted) {
                    return (
                        <>
                            <IconButton iconProps={{ iconName: 'Save' }} styles={{ root: { background: 'none', visibility: 'hidden' } }} disabled={true} />
                            <IconButton
                                iconProps={{ iconName: 'Delete' }}
                                onClick={() => {
                                    props.deletePeriod(item);
                                }}
                            />
                        </>
                    );
                }
                return (
                    <>
                        <IconButton iconProps={{ iconName: 'Save' }} styles={{ root: { background: 'none', visibility: 'hidden' } }} disabled={true} />
                        <TooltipHost
                            content={
                                <div>
                                    Period can't be removed.
                                    <br />
                                    Time entries already exists.
                                </div>
                            }
                            closeDelay={250}
                        >
                            <IconButton iconProps={{ iconName: 'Delete' }} styles={{ root: { background: 'none' } }} disabled={true} />
                        </TooltipHost>
                    </>
                );
            }
            default:
                return <span>ERROR</span>;
        }
    };

    return (
        <DetailsList
            items={periodListItems}
            columns={_columns}
            selectionMode={SelectionMode.none}
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            ariaLabelForSelectionColumn="Toggle selection"
            checkButtonAriaLabel="Row checkbox"
            groupProps={{
                showEmptyGroups: true,
            }}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            onRenderItemColumn={onRenderColumn}
            compact={true}
            styles={{
                root: {
                    maxWidth: 900,
                    margin: '10px auto',
                },
                headerWrapper: {
                    '.ms-DetailsHeader-cellName': {
                        flex: '1 1 auto',
                    },
                },
            }}
        />
    );
};
